import { z } from "zod";
import { aiModelField } from "./aiMarks";
import { TAG_NAME_MAX } from "./customerTags";
import { TAG_CUSTOMERS_NAMING } from "./agentTags";

/** tag_customers (the customer tags round, brief `docs/briefs/customer-tags-and-ticket-top.md` rule 2 and "After the red team's review"):
 *  the connector's one tool that puts customer tags on people or takes them off, one person or a list. Pure: the cap, the input, what the AI
 *  reads about it. The tool's entry is composed in `agentTools.ts` (`AGENT_TOOLS`). */

/** at most this many people a call */
export const TAG_CUSTOMERS_MAX = 200;

const name = z.string().trim().min(1).max(TAG_NAME_MAX * 2).describe("A customer tag's id, or its name in any letter case and spaces, spelled the one way (\"Gold Member\" is gold-member)");
const names = z.array(name).max(20);
/** strict: an unknown field (a "dryRun", a misspelt "tags") is refused instead of silently dropped; at least one name to add or remove */
export const TagCustomersInputSchema = z.object({
  people: z.array(z.string().trim().min(1).max(254)).min(1).max(TAG_CUSTOMERS_MAX).describe("1 to 200 people: each a customer's address or a customer id"),
  add: names.optional().describe("Customer tags to put on every person, each by id or name"),
  remove: names.optional().describe("Customer tags to take off every person, each by id or name"),
  model: aiModelField(),
}).strict().refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, { message: "Name a customer tag to add or to remove", path: ["add"] });
export type TagCustomersInput = z.infer<typeof TagCustomersInputSchema>;

/** What the AI reads about the tool, before the shared mark and model sentences. */
export const TAG_CUSTOMERS_RULES = [
  "Put customer tags on people or take them off: a customer tag is the team's label on a person (vip, beta, a plan) that every ticket from them shows; it is never put on one ticket (set_tags is for a ticket's own tags).",
  `Pass people, 1 to ${TAG_CUSTOMERS_MAX} customers each by address or customer id, and the tags to add and to remove.`,
  TAG_CUSTOMERS_NAMING,
  "Needs your member's right to edit customers. The tool never creates a customer: an address nobody wrote from, or a customer your member cannot see, is refused not_found; a colleague's address is refused team_member.",
  "The answer lists every person in your order in results, each with customerId and the names added and removed, or with refused (the code) and a message, plus the counts changed and refused.",
  "Tags are the team's labels: never mention them to the customer.",
].join(" ");
